/**
 * Modalidade de teclado (§15.2.1): escritor único de `ui.keyboard` (FE §6.6). Liga em `keydown` na captura de `window`
 * com qualquer tecla sem Ctrl, Meta ou Alt (Tab, setas, Enter, Espaço e Esc incluídos); desliga em `pointerdown` na
 * captura, com qualquer ponteiro. Valor inicial `false`, nunca persistido. Espelho em
 * `<html data-modality="keyboard|pointer">`, só para testes e para a dica X8 (o foco visível é de `:focus-visible`).
 */
import { useEffect } from 'preact/hooks';
import { ui } from '../state/store';

export type Modality = 'keyboard' | 'pointer';

function setKeyboard(on: boolean): void {
  if (ui.value.keyboard !== on) ui.value = { ...ui.value, keyboard: on };
  if (typeof document !== 'undefined') document.documentElement.dataset.modality = on ? 'keyboard' : 'pointer';
}

/** `keydown` conta como teclado só sem Ctrl, Meta ou Alt (atalhos com modificador não mudam a modalidade). */
export function isKeyboardModalityEvent(e: Pick<KeyboardEvent, 'ctrlKey' | 'metaKey' | 'altKey'>): boolean {
  return !e.ctrlKey && !e.metaKey && !e.altKey;
}

let installs = 0;
let teardown: (() => void) | null = null;

/**
 * Instala os dois ouvintes uma única vez por janela (idempotente; contagem de referências para o desmonte).
 * Devolve a função que desfaz a instalação.
 */
export function installKeyboardModality(win: Window = window): () => void {
  installs++;
  if (installs === 1) {
    const onKey = (e: KeyboardEvent) => {
      if (isKeyboardModalityEvent(e)) setKeyboard(true);
    };
    const onPointer = () => setKeyboard(false);
    win.addEventListener('keydown', onKey, true);
    win.addEventListener('pointerdown', onPointer, true);
    setKeyboard(ui.value.keyboard);
    teardown = () => {
      win.removeEventListener('keydown', onKey, true);
      win.removeEventListener('pointerdown', onPointer, true);
    };
  }
  let done = false;
  return () => {
    if (done) return;
    done = true;
    installs--;
    if (installs === 0 && teardown) {
      teardown();
      teardown = null;
    }
  };
}

/** Hook do App (bloco 5 de §2.2): instala na montagem e desinstala na desmontagem. */
export function useKeyboardModality(): void {
  useEffect(() => installKeyboardModality(), []);
}
